import { createContext, useContext, type PropsWithChildren } from 'react';
import { observer } from 'mobx-react';

import { TaskData } from 'chaika-frontend-types/frontend-cms';

import { store } from '@store';

import type { TaskFormActions, TaskFormState } from '../types';
import type { TaskPermissions } from '../utils/taskPermissions';
import { useTaskFormState } from '../hooks/useTaskFormState';
import { useTaskInit } from '../hooks/useTaskInit';
import { useTaskPermissions } from '../hooks/useTaskPermissions';

interface TaskFormContextValue {
    state: TaskFormState;
    actions: TaskFormActions;
    permissions: TaskPermissions;
    task?: TaskData;
    isLoading: boolean;
    syncTaskBaseline(patch: Partial<TaskData>): void;
}

interface Props extends PropsWithChildren {
    taskId?: string;
    patientId?: string;
    medicalCaseIds?: string[];
    entryId?: string;
}

const TaskFormContext = createContext<TaskFormContextValue | null>(null);

export const TaskFormProvider = observer(({ taskId, patientId, medicalCaseIds, entryId, children }: Props) => {
    const { state, actions } = useTaskFormState();
    const permissions = useTaskPermissions(state);
    const { task, isLoading, syncTaskBaseline } = useTaskInit(
        { taskId, patientId, medicalCaseIds, entryId: entryId ?? store.entry?.id },
        actions,
    );

    return (
        <TaskFormContext.Provider value={{ state, actions, permissions, task, isLoading, syncTaskBaseline }}>
            {children}
        </TaskFormContext.Provider>
    );
});

export function useTaskForm(): TaskFormContextValue {
    const context = useContext(TaskFormContext);

    if (!context) {
        throw new Error('useTaskForm must be used within TaskFormProvider');
    }

    return context;
}
